/** Plate-based V2 of the Engagement Letter editor — gated by engagementLetter.v2 write flag.
    Layout mirrors the One-Pager BottomDrawer (DealSummaryEditorPlate): white toolbar strip
    above a gray "Google-Docs" canvas, with a sliding Comments side-pane on the right. */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { closeEngagementLetter, setEngagementLetterData } from '../../store/investor/action';
import { fetchEngagementLetterTemplate, getOnePagerTemplateData, saveOnePagerTemplateData } from '../../store/investor/investorApiHelper';
import { fetchCompanyOverviewData } from '../../store/dashboard/overview/actions';
import { OverviewData } from '../../store/dashboard/overview/types';
import { regenerateIcon, PURPLE_DROPDOWN_CARET } from '../../assets/hostedassets';
import {
    PlateEditor,
    slateToPlateValue,
    isSlateShape,
    processImagesBeforeSave,
    collectDocIdsFromSlate,
    exportEditorToPdfRaster,
    exportEditorToPdf,
} from '../PlateEditor';
import { toast } from 'react-toastify';
import { Button, Modal } from '../_custom';
import { BottomDrawer } from '../BottomDrawer';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import { initializeTemplateWithMetadata } from '../../utils/initializeTemplateWithMetadata';
import { DATE_FORMAT } from '../../utils/constants/commonConstants';
import { Mixpanel } from '../../utils/mixpanel';
import { getSubdomain } from '../../utils/utils';
import { AICA_ENGAGEMENT_LETTER_SAVE } from '../../utils/constants/mixpanelEvents/investorEvents';
import { resetAndHideCommentsThread, closeCommentsThread } from '../../store/commentsThread/commentsThread_actions';
import styles from './EngagementLetterV2.module.scss';

function EngagementLetterV2() {
    const dispatch = useAppDispatch();
    const engagementLetterData = useAppSelector((state) => state.investor.engagementLetter);
    const ownerOptions = useAppSelector((state) => state.investor.ownerDropdownOptions) || [];
    // Comments side-pane open state — mirrors DealSummaryEditorPlate so the
    // CommentsThread portals from a single source of truth in Redux.
    const commentsThreadState = useAppSelector((state) => state.commentsThreadReducer);
    const commentsActive = Boolean(commentsThreadState?.showCommentsThread && !commentsThreadState?.closingCommentsThread);

    const orgId = engagementLetterData.orgId ?? '';
    const companyName = engagementLetterData.companyName ?? '';

    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);
    const emailId = useAppSelector((state) => state.user.userData.primaryEmail);
    const user = useAppSelector((state) => state.user.userData);
    const companyMainImg = useAppSelector((state) => state.user.domainMetadata?.mainImageUrl);

    const [editorInitialData, setEditorInitialData] = useState<any>(null);
    const [editorReady, setEditorReady] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showRegenConfirmModal, setShowRegenConfirmModal] = useState(false);
    const [exporting, setExporting] = useState(false);
    // Bumped on regenerate to force-remount the PlateEditor so it picks up
    // the freshly-fetched template as its `initialValue` (the editor only
    // reads initialValue on mount, like the one-pager pattern).
    const [editorKey, setEditorKey] = useState(0);

    const editorContentRef = useRef<any[]>([]);
    const originalDocIdsRef = useRef<string[]>([]);
    // exportEditorToPdf rasterizes a DOM element via html2canvas-pro, so we
    // need a ref to the canvas (the wrapper around the editor page). Same
    // pattern as DealSummaryEditorPlate.
    const canvasRef = useRef<HTMLDivElement>(null);

    // Fallback: fetch fresh template from company overview + fetchEngagementLetterTemplate
    // when no saved document exists yet. `isRegenerate=true` skips the
    // editorReady reset so the drawer chrome doesn't blink on regenerate.
    const fetchDataForDoc = useCallback(
        (isRegenerate = false) => {
            if (!isRegenerate) {
                setEditorReady(false);
            }
            dispatch(setEngagementLetterData({ loading: true }));
            dispatch(
                fetchCompanyOverviewData({
                    assesseeOrgId: orgId,
                    investorOrgId: investorOrgId as string,
                    onSuccess: async (data: OverviewData) => {
                        const template = await fetchEngagementLetterTemplate({
                            orgId,
                            accountType: 'INVESTOR',
                            pnoId: investorOrgId as string,
                            primaryEmail: emailId as string,
                        });
                        const initialized = initializeTemplateWithMetadata(template || [], {
                            pnoLogo: companyMainImg as string,
                            companyName: companyName ?? '',
                            companyAddress: data?.headquarterLocation?.fullAddress ?? '',
                            userName: '',
                            userDesignation: '',
                            userEmail: '',
                            currentDate: dayjs().format(DATE_FORMAT),
                        });
                        setEditorInitialData(initialized);
                        originalDocIdsRef.current = collectDocIdsFromSlate(initialized);
                        editorContentRef.current = initialized;
                        setEditorKey((k) => k + 1);
                        setEditorReady(true);
                        dispatch(setEngagementLetterData({ loading: false }));
                    },
                    onError: () => {
                        setEditorReady(true);
                        dispatch(setEngagementLetterData({ loading: false }));
                    },
                }),
            );
        },
        [orgId, investorOrgId, emailId, companyMainImg, companyName, dispatch],
    );

    // Primary load: check for an already-saved ENGAGEMENT_LETTER document first.
    // Falls back to fetchDataForDoc when none exists.
    const fetchTemplateData = useCallback(() => {
        setEditorReady(false);
        dispatch(setEngagementLetterData({ loading: true }));
        getOnePagerTemplateData({
            assesseeOrgId: orgId,
            investorOrgId: investorOrgId as string,
            docType: 'ENGAGEMENT_LETTER',
        })
            .then(({ templateData }) => {
                if (templateData?.length) {
                    setEditorInitialData(templateData);
                    originalDocIdsRef.current = collectDocIdsFromSlate(templateData);
                    setEditorReady(true);
                    dispatch(setEngagementLetterData({ loading: false }));
                } else {
                    fetchDataForDoc();
                }
            })
            .catch(() => {
                fetchDataForDoc();
            });
    }, [orgId, investorOrgId, dispatch, fetchDataForDoc]);

    useEffect(() => {
        dispatch(resetAndHideCommentsThread());
        fetchTemplateData();
        return () => {
            dispatch(closeEngagementLetter());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (orgId) {
            fetchTemplateData();
        }
    }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Convert Slate-shape legacy docs to Plate exactly once at load time.
    // Already-Plate documents pass through untouched.
    const initialValue = useMemo(() => {
        if (!Array.isArray(editorInitialData) || editorInitialData.length === 0) return undefined;
        return (isSlateShape(editorInitialData) ? slateToPlateValue(editorInitialData) : editorInitialData) as any;
    }, [editorInitialData]);

    const handlePlateChange = useCallback((plateValue: any) => {
        editorContentRef.current = plateValue;
    }, []);

    const handleSaveAndClose = useCallback(async () => {
        if (saving) return;
        const content = editorContentRef.current?.length
            ? editorContentRef.current
            : Array.isArray(editorInitialData)
            ? editorInitialData
            : [];
        if (!content.length) {
            dispatch(closeCommentsThread());
            dispatch(closeEngagementLetter());
            return;
        }
        setSaving(true);
        try {
            const processed = await processImagesBeforeSave(content, originalDocIdsRef.current, 'engagementLetter');
            originalDocIdsRef.current = collectDocIdsFromSlate(processed);
            await saveOnePagerTemplateData({
                assesseeOrgId: orgId,
                investorOrgId: investorOrgId as string,
                onePagerData: processed,
                email: emailId as string,
                docType: 'ENGAGEMENT_LETTER',
            });
            Mixpanel.track(AICA_ENGAGEMENT_LETTER_SAVE, {
                userRole: user?.accountType,
                pnoPlatform: getSubdomain(),
                userId: user?.userId,
                currentUrl: window.location.href,
                organizationId: orgId,
            });
        } finally {
            setSaving(false);
            dispatch(closeCommentsThread());
            dispatch(closeEngagementLetter());
        }
    }, [saving, editorInitialData, orgId, investorOrgId, emailId, user, dispatch]);

    const isLoading = !!engagementLetterData.loading || !editorReady;
    const headerTitle = `Engagement Letter${companyName ? ` — ${companyName}` : ''}`;

    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const exportMenuRef = useRef<HTMLDivElement>(null);

    // Close export menu on outside click / Escape
    useEffect(() => {
        if (!exportMenuOpen) return;
        const onDocClick = (e: MouseEvent) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
                setExportMenuOpen(false);
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setExportMenuOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [exportMenuOpen]);

    // html2canvas-pro raster PDF export — the same pipeline the one-pager uses
    // for its default "Export .pdf" (exportEditorToPdfRaster). Captures the live
    // editor DOM, resolves docId images to fresh base64, and slices the canvas
    // across A3 pages. Sanitises the filename (strips path-illegal chars,
    // collapses whitespace to underscores).
    const safeExportName = () => {
        const base = `Engagement_Letter${companyName ? `_${companyName}` : ''}`;
        return base.replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_');
    };
    const handleExportPdf = async () => {
        if (!canvasRef.current || exporting) return;
        setExporting(true);
        try {
            await exportEditorToPdfRaster({
                target: canvasRef.current,
                filename: safeExportName(),
            });
        } catch {
            toast.error('PDF export failed. Please try again.');
        } finally {
            setExporting(false);
        }
    };
    // Browser-print PDF — paginated via the print pipeline (exportEditorToPdf is
    // the print exporter). Mirrors the one-pager's "Export to pdf (Print)" item.
    const handlePrint = async () => {
        if (!canvasRef.current || exporting) return;
        setExporting(true);
        try {
            await exportEditorToPdf({
                target: canvasRef.current,
                filename: safeExportName(),
            });
        } catch {
            toast.error('Print export failed. Please try again.');
        } finally {
            setExporting(false);
        }
    };
    const headerActions = (
        <>
            <div className={styles.exportGroup} ref={exportMenuRef}>
                <button
                    type="button"
                    className={styles.exportBtn}
                    onClick={handleExportPdf}
                    disabled={isLoading || exporting}
                    aria-label="Export as PDF"
                    title="Export the engagement letter as a paginated PDF"
                >
                    <span>{exporting ? 'Exporting…' : 'Export .pdf'}</span>
                </button>
                <button
                    type="button"
                    className={styles.exportCaretBtn}
                    onClick={() => setExportMenuOpen((o) => !o)}
                    disabled={isLoading || exporting}
                    aria-label="Export options"
                    aria-haspopup="true"
                    aria-expanded={exportMenuOpen}
                    title="Other export formats"
                >
                    <img src={PURPLE_DROPDOWN_CARET} className={styles.exportCaret} alt="" aria-hidden />
                </button>
                {exportMenuOpen && (
                    <div className={styles.exportPopover} role="menu" aria-label="Export options">
                        <button
                            type="button"
                            role="menuitem"
                            className={styles.exportPopoverItem}
                            onClick={() => {
                                setExportMenuOpen(false);
                                handleExportPdf();
                            }}
                            disabled={isLoading || exporting}
                            title="Export the engagement letter as a paginated PDF"
                        >
                            Export as .pdf
                        </button>
                        <button
                            type="button"
                            role="menuitem"
                            className={styles.exportPopoverItem}
                            onClick={() => {
                                setExportMenuOpen(false);
                                handlePrint();
                            }}
                            disabled={isLoading || exporting}
                            title="Export via the browser print dialog (paginated)"
                        >
                            Export to pdf (Print)
                        </button>
                    </div>
                )}
            </div>
            <Button
                text={saving ? 'Saving…' : 'Save & Close'}
                variant="primary"
                onClick={handleSaveAndClose}
                style={{ marginTop: '0' }}
                loading={saving}
            />
        </>
    );

    return (
        <>
            <BottomDrawer
                open
                onClose={() => dispatch(closeEngagementLetter())}
                title={headerTitle}
                id="engagement-letter-plate"
                headerRight={headerActions}
                height="100vh"
                className={styles.drawer}
                bodyClassName={styles.body}
            >
                <div className={styles.toolbarHost} id="el-v2-toolbar-host" />
                <div className={styles.editorRow}>
                    <div className={styles.canvas} ref={canvasRef}>
                        {isLoading ? (
                            <div className={styles.loadingPane}>
                                <LoadingSpinner />
                            </div>
                        ) : (
                            <PlateEditor
                                key={editorKey}
                                initialValue={initialValue}
                                className={styles.editor}
                                onChange={handlePlateChange}
                                fullHeight
                                toolbarHostId="el-v2-toolbar-host"
                                uploadSource="engagement-letter"
                                mentionableUsers={ownerOptions}
                            />
                        )}
                    </div>
                    {/* Comments side-pane — slides in from the right (width 0 → 360px)
                        when CommentsThread is opened against the EL surface. Same
                        portal pattern as the one-pager drawer. */}
                    <aside className={clsx(styles.commentsPane, commentsActive && styles.commentsPaneOpen)} aria-hidden={!commentsActive}>
                        <div id="el-comment-container" className={styles.commentsPortalTarget} />
                    </aside>
                </div>
                {/* Footer — Regenerate only, left-anchored as a body-styled
                    inline link (mirrors the legacy one-pager modal's `.Regen`
                    pattern). Save & Close + Export .pdf live in headerRight. */}
                <div className={styles.footer}>
                    <button
                        type="button"
                        className={styles.regenerateBtn}
                        onClick={() => setShowRegenConfirmModal(true)}
                        disabled={isLoading}
                        aria-label="Regenerate engagement letter"
                        title="Regenerate engagement letter"
                    >
                        <img src={regenerateIcon} alt="" aria-hidden height="24" />
                        <span>Regenerate</span>
                    </button>
                </div>
            </BottomDrawer>

            {showRegenConfirmModal && (
                <Modal
                    open={showRegenConfirmModal}
                    onClose={() => setShowRegenConfirmModal(false)}
                    size="flexible"
                    className={styles.regenModalOverDrawer}
                >
                    <div className={styles.confirmModal}>
                        <div className={styles.confirmTitle}>Regenerate Engagement Letter</div>
                        <div className={styles.confirmSubtext}>
                            Are you sure you want to regenerate the Engagement Letter? You will lose all the changes made so far.
                        </div>
                        <div className={styles.confirmButtons}>
                            <Button
                                text="Cancel"
                                variant="purpleinverted"
                                onClick={() => setShowRegenConfirmModal(false)}
                                style={{ marginTop: '0' }}
                            />
                            <Button
                                text="Proceed Anyway"
                                variant="primary"
                                onClick={() => {
                                    setShowRegenConfirmModal(false);
                                    fetchDataForDoc(true);
                                }}
                                style={{ marginTop: '0' }}
                            />
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
}

export default EngagementLetterV2;
